import type { TmsInvoiceStatusGroup, TmsLoadRow, TmsLoadStage } from '@numeo/types-main-web-app'
import { formatCurrency } from '@/utils/formatters'

/** Money as the cards carry it: dollars, cents shown; unknown is a dash, never zero. */
export const formatUsd = (value: number | null | undefined): string => (value === null || value === undefined ? '—' : formatCurrency(value, 2))

/** A count as the chat writes it, with thousands separators: "1,471". */
export const formatCount = (value: number): string => value.toLocaleString('en-US')

/** "1 load", "1,471 loads": a count of loads with its noun. */
export const loadsCount = (value: number): string => `${formatCount(value)} ${value === 1 ? 'load' : 'loads'}`

/** The Invoices page's tab names for the cards' status groups. */
export const STATUS_GROUP_LABEL: Record<TmsInvoiceStatusGroup, string> = {
    ready_to_invoice: 'Ready to invoice',
    pending: 'Pending',
    invoiced: 'Invoiced',
    funded_by_factor: 'Funded by factor',
    paid_by_broker: 'Paid by broker',
    issues: 'Issues',
}

/** The Ongoing Loads board's tab names for the load stages. */
export const LOAD_STAGE_LABEL: Record<TmsLoadStage, string> = {
    upcoming: 'Upcoming',
    dispatched: 'Dispatched',
    in_transit: 'In transit',
    delivered: 'Delivered',
    cancelled: 'Cancelled',
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const

/**
 * A calendar date as the chat shows it: "Oct 26, 2026", never a locale string with a clock. A `YYYY-MM-DD` value is read as
 * written (no zone shift); a timestamp is shown in the viewer's zone. Unknown is a dash.
 */
export const formatDate = (value: string | Date | null | undefined): string => {
    if (!value) return '—'
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split('-').map(Number)
        return `${MONTH_SHORT[month - 1]} ${day}, ${year}`
    }
    const date = value instanceof Date ? value : new Date(value)
    if (Number.isNaN(date.getTime())) return '—'
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/**
 * A span of calendar days (`YYYY-MM-DD`, read as written) as the chat shows it: "Oct 27 – Nov 2, 2026", the year once when both days
 * share it, "Dec 29, 2026 – Jan 4, 2027" otherwise. Unknown is a dash.
 */
export const formatDayRange = (from: string, to: string): string => {
    const day = /^(\d{4})-(\d{2})-(\d{2})$/
    const start = day.exec(from)
    const end = day.exec(to)
    if (!start || !end) return '—'
    const short = (parts: RegExpExecArray) => `${MONTH_SHORT[Number(parts[2]) - 1]} ${Number(parts[3])}`
    if (start[1] !== end[1]) return `${formatDate(from)} – ${formatDate(to)}`
    return `${short(start)} – ${short(end)}, ${end[1]}`
}

/** A load's lane, from what the card knows of it. */
export const laneOf = (origin: string | null, destination: string | null): string => [origin ?? '?', destination ?? '?'].join(' → ')

/** A delivered load's invoice cell: its invoice number, "Invoiced" when the number is unknown, else "Not invoiced". */
export const invoiceCellOf = (row: TmsLoadRow): string => {
    if (row.status !== 'delivered') return '—'
    if (!row.invoice) return 'Not invoiced'
    return row.invoice.invoice_number ?? 'Invoiced'
}
